import React from 'react';
import { useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import AlertComponent from '../../../../utility/AlertComponent';
import IAlertType from '../../../../../utils/types/props/IAlertType';
import apiClient from '../../../../../services/api/apiClient';
import { formatAxiosError } from '../../../../../utils/formatAxiosError';
import { Carte } from '../../../../../utils/types/Carte';
import { useAuth } from '../../../../../utils/context/AuthContext';
import { IconEdit } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';

//#region interfaces

interface EditCarteProps {
    carte: Carte;
    onUpdate?: (updatedCarte: Carte) => void;
    setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

//#endregion

const EditCarteModalContent: React.FC<EditCarteProps> = ({ carte, onUpdate, setShowModal }) => {
    const statutList = ['Actif', 'Inactif'];
    const { user } = useAuth();

    //#region useState

    const [limitePrise, setLimitePrise] = useState<number | null>(carte.limitePrise);
    const [limiteMensuelEs, setLimiteMensuelEs] = useState<number | null>(carte.limiteMensuelEs);
    const [limiteMensuelGo, setLimiteMensuelGo] = useState<number | null>(carte.limiteMensuelGo);
    const [codePin, setCodePin] = useState<string | null>(carte.codePin);
    const [noLimitUpToMonthLimit, setNoLimitUpToMonthLimit] = useState<boolean | null>(carte.noLimitUpToMonthLimit);
    const [selectedStatut, setSelectedStatut] = useState<string | null>(carte.statut);

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };
    const addAlert = (type: IAlertType, message: string) => {
        setSystemAlerts((prevAlerts) => [...prevAlerts, { type, message }]);
    };

    // ---

    const { mutate, isPending: isSubmittingOperation } = useMutation({
        mutationFn: (password: string) =>
            apiClient.post('/api/cartes/update', {
                carteId: carte.id,
                limitePrise,
                limiteMensuelEs,
                limiteMensuelGo,
                codePin,
                noLimitUpToMonthLimit,
                statut: selectedStatut,
                userPassword: password,
            }),
        onSuccess: (response) => {
            if (response.data.success === true) {
                onUpdate?.(response.data.data);

                Swal.fire({
                    text: 'Opération effectuée avec succès.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    setShowModal?.(false);
                });
            } else {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: response.data.message,
                    },
                ]);
            }
        },
        onError: (err) => {
            addAlert('danger', formatAxiosError(err)?.message ?? "Erreur lors de l'opération.");
        },
    });

    // ---

    //#endregion

    //#region methods

    const submitForm = () => {
        Swal.fire({
            title: 'Confirmation',
            text: 'Veuillez entrer votre mot de passe pour confirmer :',
            input: 'password',
            inputAttributes: {
                autocapitalize: 'off',
                autocorrect: 'off',
                placeholder: 'Entrez votre mot de passe',
            },
            showCancelButton: true,
            confirmButtonText: 'Confirmer',
            cancelButtonText: 'Annuler',
            showLoaderOnConfirm: true,
            preConfirm: (password) => {
                if (!password) {
                    Swal.showValidationMessage('Le mot de passe est requis');
                    return false;
                }
                return password;
            },
        }).then((result) => {
            if (result.isConfirmed) {
                mutate(result.value);
            }
        });
    };

    //#endregion

    return (
        <>
            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />

            <div>
                <form action="#">
                    <div className="row">
                        <div className="col-md-12 form-group">
                            <label>Limite Prise</label>
                            <input type="number" className="form-control" value={limitePrise ?? ''} onChange={(e) => setLimitePrise(e.target.value === '' ? null : Number(e.target.value))} />
                        </div>

                        <div className="col-md-12 form-group">
                            <label>Limite mens. Essence</label>
                            <input type="number" className="form-control" value={limiteMensuelEs ?? ''} onChange={(e) => setLimiteMensuelEs(e.target.value === '' ? null : Number(e.target.value))} />
                        </div>

                        <div className="col-md-12 form-group">
                            <label>Limite mens. Gasoil</label>
                            <input type="number" className="form-control" value={limiteMensuelGo ?? ''} onChange={(e) => setLimiteMensuelGo(e.target.value === '' ? null : Number(e.target.value))} />
                        </div>

                        <div className="hr-text">#</div>

                        <div className="col-md-12 form-group">
                            <label>Code PIN</label>
                            <input type="text" className="form-control" value={codePin ?? ''} onChange={(e) => setCodePin(e.target.value === '' ? null : e.target.value)} />
                        </div>

                        {user?.roles?.includes('Admin') && (
                            <>
                                <div className="hr-text">#</div>

                                <div>
                                    <label htmlFor="notLimitUpToMonthLimit">
                                        <input id="notLimitUpToMonthLimit" type="checkbox" checked={noLimitUpToMonthLimit ?? false} onChange={(e) => setNoLimitUpToMonthLimit(e.target.checked)} /> Permettre livraison sans recharge
                                    </label>
                                </div>
                            </>
                        )}

                        <div className="hr-text">#</div>

                        <div className="col-md-12 form-group">
                            <label>Statut</label>
                            <select
                                value={selectedStatut ?? ''}
                                onChange={(e) => {
                                    setSelectedStatut(e.target.value);
                                }}
                                className="form-select"
                            >
                                {statutList.map((statut, index) => (
                                    <option key={index} value={statut}>
                                        {statut}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="text-end mt-3">
                        <button
                            type="button"
                            className="btn btn-warning"
                            onClick={() => {
                                submitForm();
                            }}
                            disabled={isSubmittingOperation}
                        >
                            {isSubmittingOperation ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    &nbsp; Valider
                                </>
                            ) : (
                                `Valider`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

const EditCarteModal: React.FC<EditCarteProps> = ({ carte, onUpdate }) => {
    const [showModal, setShowModal] = useState(false);

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    // ---

    return (
        <>
            <button type="button" className="btn btn-1" onClick={handleOpenModal}>
                <IconEdit size={16} color="currentColor" className="icon icon-tabler icons-tabler-outline icon-tabler-edit" />
                Editer la carte
            </button>

            <Modal show={showModal} onHide={handleCloseModal} className="fade modal-blur" centered scrollable backdrop="static" size="sm" enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Modifier la carte</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <EditCarteModalContent carte={carte} onUpdate={onUpdate} setShowModal={setShowModal} />
                </Modal.Body>
            </Modal>
        </>
    );
};

export default EditCarteModal;
